import React, { useState } from "react";
import { Clock, Moon } from "lucide-react";

const HAPPY_HOUR = [
  {
    id: "hh-lonestar",
    name: "Lone Star / PBR Cans",
    desc: "The ultimate post-work tallboy crush.",
    descColor: "text-[#FFB800]",
    price: "$2.50",
    priceColor: "text-[#FFB800]",
  },
  {
    id: "hh-well",
    name: "Well Drinks",
    desc: "Vodka, gin, rum, bourbon — heavy pours only.",
    descColor: "text-[#A3A3A3]",
    price: "$4.00",
    priceColor: "text-[#F5F2E6]",
  },
  {
    id: "hh-jello",
    name: "Famous Neon Jell-O Shots",
    desc: "Downtown's #1 calling card.",
    descColor: "text-[#FF2A2A] font-semibold",
    price: "$1.00",
    priceColor: "text-[#FF2A2A]",
  },
];

const LATE_NIGHT = [
  {
    id: "ln-lonestar",
    name: "Lone Star / PBR Cans",
    desc: "Classic Texas premium lager staple.",
    descColor: "text-[#A3A3A3]",
    price: "$3.50",
    priceColor: "text-[#F5F2E6]",
  },
  {
    id: "ln-craft",
    name: "Local Houston Craft Cans",
    desc: "Rotating local independent brews.",
    descColor: "text-[#A3A3A3]",
    price: "$6.00",
    priceColor: "text-[#F5F2E6]",
  },
  {
    id: "ln-well",
    name: "Well Drinks",
    desc: "Clean pours, no frills speed-rail items.",
    descColor: "text-[#A3A3A3]",
    price: "$5.00",
    priceColor: "text-[#F5F2E6]",
  },
  {
    id: "ln-setup",
    name: "The Setup (Beer + Shot Combo)",
    desc: "A cold tallboy paired with a house whiskey shot.",
    descColor: "text-[#FFB800]",
    price: "$8.00",
    priceColor: "text-[#FFB800]",
  },
  {
    id: "ln-jello",
    name: "Famous Neon Jell-O Shots",
    desc: "Grab a round for the table.",
    descColor: "text-[#FF2A2A]",
    price: "$1.00",
    priceColor: "text-[#FF2A2A]",
  },
];

const TABS = [
  {
    id: "happy",
    label: "Happy Hour",
    sub: "(2 PM – 7 PM)",
    icon: <Clock size={14} strokeWidth={2.5} />,
    activeBg: "bg-[#FFB800] text-black",
    items: HAPPY_HOUR,
  },
  {
    id: "late",
    label: "Late Night",
    sub: "(Staples)",
    icon: <Moon size={14} strokeWidth={2.5} />,
    activeBg: "bg-[#FF2A2A] text-white",
    items: LATE_NIGHT,
  },
];

const MenuItemRow = ({ item, last }) => (
  <div
    data-testid={`menu-row-${item.id}`}
    className={`flex justify-between items-center py-3.5 ${
      last ? "" : "border-b border-[#262626]"
    }`}
  >
    <div className="text-left min-w-0 pr-4">
      <span className="block font-display text-lg md:text-xl text-white tracking-wide">
        {item.name}
      </span>
      <span className={`text-xs ${item.descColor} font-mono-plex`}>
        {item.desc}
      </span>
    </div>
    <span
      className={`font-display text-2xl md:text-3xl tabular-nums ${item.priceColor}`}
    >
      {item.price}
    </span>
  </div>
);

const MenuTabButton = ({ tab, isActive, onClick }) => (
  <button
    role="tab"
    aria-selected={isActive}
    data-testid={`menu-tab-${tab.id}`}
    onClick={onClick}
    className={`flex-1 sm:flex-none px-4 sm:px-5 py-2.5 rounded-md font-mono-plex text-xs sm:text-sm uppercase tracking-[0.18em] font-bold transition-all duration-200 flex items-center justify-center gap-2 ${
      isActive
        ? `${tab.activeBg} shadow-md`
        : "text-[#A3A3A3] hover:text-[#F5F2E6]"
    }`}
  >
    {tab.icon}
    <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-1.5">
      {tab.label}
      <span
        className={`text-[9px] sm:text-[10px] font-normal ${
          isActive ? "opacity-80" : "opacity-60"
        }`}
      >
        {tab.sub}
      </span>
    </span>
  </button>
);

const MenuHeader = () => (
  <div className="space-y-3">
    <span className="text-[#FFB800] font-mono-plex text-xs font-bold tracking-[0.3em] uppercase">
      Cold Drinks
    </span>
    <h2
      data-testid="menu-heading"
      className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight uppercase leading-[0.95]"
    >
      The <span className="text-stroke">Liquid</span> Ledger
    </h2>
    <p className="text-[#A3A3A3] max-w-md mx-auto text-sm md:text-base">
      No taps, no fuss. Just ice-cold bottles, cans, heavy pours, and
      legendary setups.
    </p>
  </div>
);

const TabSwitcher = ({ active, onChange }) => (
  <div
    data-testid="menu-tab-switcher"
    role="tablist"
    className="inline-flex rounded-md p-1 bg-[#1A1A1A] border border-[#333]"
  >
    {TABS.map((t) => (
      <MenuTabButton
        key={t.id}
        tab={t}
        isActive={active === t.id}
        onClick={() => onChange(t.id)}
      />
    ))}
  </div>
);

const MenuCard = ({ active, items }) => (
  <div
    className="bg-[#1A1A1A] rounded-xl p-6 sm:p-8 border border-[#252525] relative min-h-[320px] text-left"
    style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}
  >
    <div
      key={active}
      data-testid={`menu-list-${active}`}
      className="rise space-y-0"
    >
      {items.map((item, i) => (
        <MenuItemRow
          key={item.id}
          item={item}
          last={i === items.length - 1}
        />
      ))}
    </div>
  </div>
);

const KoozieHook = () => (
  <p
    data-testid="koozie-hook"
    className="text-xs font-mono-plex text-[#777] italic"
  >
    ⚡ Pop in, grab an ice-cold can, and ask the crew for our legendary{" "}
    <span className="text-white not-italic font-bold">
      "Stolen from Sunny's"
    </span>{" "}
    koozie.
  </p>
);

const MenuTable = () => {
  const [active, setActive] = useState("happy");
  const tab = TABS.find((t) => t.id === active);

  return (
    <section
      id="menu"
      data-testid="menu-table"
      className="relative bg-[#121212] text-white border-y border-[#262626] px-4 md:px-12 lg:px-24 py-20 md:py-28"
    >
      <div className="max-w-4xl mx-auto text-center space-y-8">
        <MenuHeader />
        <TabSwitcher active={active} onChange={setActive} />
        <MenuCard active={active} items={tab.items} />
        <KoozieHook />
      </div>
    </section>
  );
};

export default MenuTable;
